import CustomDataGridResult from '../../../../utils/Datagrid/CustomForecastDataGridResult';
import { useCallback, useState } from 'react';
import { parcelSynthesisTheme } from '../../../../utils/Datagrid/CustomForecastDataGridUtils';
import { checkObjectsEquality, getColorAverageBiomassInput } from 'utils/tools_functions';
import { CustomSimulationCell, CustomEditSelectCell } from './CustomSimulationDataGrid';
import bioLogo from 'assets/images/bio.png';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { StyledTooltip } from 'utils/Datagrid/CustomGenericDataGridUtils';

const DataGridParcelSynthesis = ({
  rows,
  columns,
  cultures,
  setOther,
  setPac,
  setPacHelps,
  defaultOtherTable,
  irrigationInformation,
  setHasChange,
  suggest,
  setSuggest,
  editedSynthesisCellsRef,
  isNitrogenASelectedConstraint,
}) => {
  const [selectIndex, setSelectIndex] = useState(null);

  const renderSelectEditCell = (params) => {
    return (
      <CustomEditSelectCell
        params={params}
        culturesList={cultures}
        selectIndex={selectIndex}
        isNitrogenASelectedConstraint={isNitrogenASelectedConstraint}
      />
    );
  };

  const processedColumns = columns?.map((column, index) => {
    let processedColumn = {
      field: column,
      headerName: column,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      cellClassName: () => {
        if (index === 2) {
          return 'MuiDataGrid-colCellSynthesis MuiDataGrid-colCellSynthesis-parcelle';
        }
      },
    };

    if (column === 'Parcelle') {
      processedColumn = {
        ...processedColumn,
        cellClassName: 'MuiDataGrid-colCellSynthesis-parcelle',
        renderCell: (params) => {
          const row = rows?.find((row) => row.id === params.id);
          return (
            <span>
              {params.value} {row?.isParent && <span>*</span>}
            </span>
          );
        },
      };
    }

    if (index === 0 || index === 2) {
      processedColumn.minWidth = 170;
      processedColumn.maxWidth = 170;
    }
    if (index === 1) {
      processedColumn.minWidth = 110;
      processedColumn.maxWidth = 110;
      processedColumn.valueFormatter = (value) => {
        if (typeof value === 'number') {
          return Number(value.toFixed(2));
        }
        return value;
      };
    }

    if (index === 2) {
      processedColumn = {
        ...processedColumn,
        index: index - 3,
        renderCell: (params) => {
          return (
            <div className="cell-box">
              <div className="cell-main-content">
                {params.value}
                <div className="cultures-icons-wrapper">
                  <div className="culture-infos-icon">
                    <StyledTooltip
                      title={`${
                        params.row?.organicState === 'conversion'
                          ? 'Culture bio en conversion'
                          : params.row?.organicState === 'organic'
                          ? 'Culture bio'
                          : ''
                      }`}
                      placement="top"
                    >
                      {params.row?.organicState === 'conversion' ? (
                        <SyncAltIcon className="dropletLogo" />
                      ) : params.row?.organicState === 'organic' ? (
                        <img src={bioLogo} alt="Logo organic" className="dropletLogo" />
                      ) : null}
                    </StyledTooltip>
                  </div>
                </div>
              </div>
            </div>
          );
        },
      };
    }

    if (index >= 3) {
      processedColumn = {
        ...processedColumn,
        index: index - 3,
        editable:
          index !== columns.length - 1 || column !== 'Qté MS moyenne apportée par campagne'
            ? true
            : false,
        minWidth: 180,
        renderCell: (params) =>
          CustomSimulationCell(params, editedSynthesisCellsRef, setSelectIndex),
        renderEditCell: renderSelectEditCell,
        cellClassName: (params) => {
          return `${params.row.isParcelDivision}` ? 'MuiDataGrid-colCellSynthesis-division' : '';
        },
      };

      if (column === 'Qté MS moyenne apportée par campagne') {
        processedColumn = {
          ...processedColumn,
          cellClassName: (params) => {
            if (params.field === 'Qté MS moyenne apportée par campagne') {
              return getColorAverageBiomassInput(params.row.averageBiomassInput);
            }
            return '';
          },
        };
      }
    }

    return processedColumn;
  });

  const processedRows = rows?.map((row, index) => {
    let processedRow = {
      id: row?.id,
      organicState: row?.organicState,
    };

    irrigationInformation?.map((year) => {
      if (year?.irrigationDistribution?.[index] > 0) {
        processedRow = {
          ...processedRow,
          isIrrigated: true,
        };
      }
    });

    processedColumns?.forEach((column, idx) => {
      switch (column.field) {
        case 'Parcelle':
          processedRow[column?.field] = row?.name;
          break;
        case 'Surface':
          processedRow[column?.field] = row?.surface;
          break;
        case 'Qté MS moyenne apportée par campagne':
          const averageBiomassInput = row?.averageBiomassInput || 0;
          processedRow['averageBiomassInput'] = averageBiomassInput;
          processedRow[column?.field] = averageBiomassInput + ' ' + 't MS/ha';
          break;
        default:
          if (idx === 2) {
            processedRow[column?.field] = row?.previousCulture1;
          }
          if (idx > 2) {
            const cultureByIndex = row?.cultures[idx - 3];
            const isParcelDivision = Array.isArray(cultureByIndex) && cultureByIndex?.length > 1;
            processedRow[column?.field] = {
              isParcelDivision,
              cultures: cultureByIndex ? cultureByIndex : [],
            };
          }
      }
    });
    return processedRow;
  });

  const processRowUpdate = useCallback(
    (updatedRow, previousRow) => {
      if (!checkObjectsEquality(updatedRow, previousRow)) {
        const simulatedCultures = Object.entries(updatedRow).filter(
          ([_, value]) => typeof value === 'object'
        );

        const simulatedCulturesObject = Object.fromEntries(simulatedCultures);

        const simulatedCulturesArray = Object.entries(simulatedCulturesObject).map(
          (entry) => entry[1].cultures
        );

        const flattenedCulturesArray = simulatedCulturesArray.flat();

        const editedCell = flattenedCulturesArray.find(
          (editedCulture) => editedCulture?.isEdited === true
        );

        if (!editedSynthesisCellsRef.current.includes(editedCell?.cellId)) {
          editedSynthesisCellsRef.current.push(editedCell?.cellId);
        }

        const updatedSuggest = { ...suggest };

        let simulatedCulturesArrayClone = structuredClone(simulatedCulturesArray);

        const foundParcel = updatedSuggest.parcels.find((parcel) => parcel.id === updatedRow.id);

        simulatedCulturesArrayClone.forEach((cultures) => {
          cultures.forEach((culture) => {
            if (culture.cellId) {
              delete culture.cellId;
            }
            if (culture.isEdited) {
              delete culture.isEdited;
            }
          });
        });

        foundParcel.cultures = simulatedCulturesArrayClone;

        setSuggest(updatedSuggest);
        setOther(JSON.parse(JSON.stringify(defaultOtherTable)));
        setPac([]);
        setPacHelps([]);
        setHasChange(true);

        return updatedRow;
      }

      return previousRow;
    },
    [rows]
  );

  const getRowClassName = () => 'Mui-even';

  return (
    <div className="datagrid_container">
      <CustomDataGridResult
        rows={processedRows}
        columns={processedColumns}
        getRowClassName={getRowClassName}
        processRowUpdate={processRowUpdate}
        theme={parcelSynthesisTheme}
        simulationContext={'parcel-synthesis-box'}
        fullSize={true}
        fromSimulation
      />
    </div>
  );
};

export default DataGridParcelSynthesis;
